// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import { mdiDownload } from "@mdi/js";
import { Popover } from "antd";
import { RootState } from "store/rootReducer";
import {
  selectCheckedGlobalTypeWells,
  selectSelectedTypeWell,
  useForecastStore
} from "stores/forecast";
import styled from "styled-components";

import { useUser, useUserEvents, useUserSettingFetcher } from "hooks";

import { ExportForecastRequest, TypeWellId, exportForecasts } from "api/userForecasts";

import { Tooltip } from "components/base";
import { IconSpinner } from "components/icons";
import { FORECAST_EXPORT_SETTING } from "components/user-settings/constants/userSetting.constants";
import { ForecastExportSetting } from "components/user-settings/models/ForecastExportSetting";

const exportFile = (response) => {
  const contentDisposition = response?.headers["content-disposition"];
  // Make sure content-disposition exists and that the filename
  // contains 'McDanielForecasts' which is the naming standard produced from the backend
  if (response?.data && contentDisposition) {
    const parts = contentDisposition.split("filename*=UTF-8''");
    if (parts.length >= 2) {
      const fileName = parts[1];
      const encodedUri = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = encodedUri;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }
  }
  toast.error("Failed to export forecast");
};

export const EXPORT_ERROR_FALLBACK = "Failed to export forecasts. Please try again.";

// The shape this function actually depends on. `onError` hands us `unknown`
// and tsconfig has `strict: false`, so an untyped parameter would silently be
// `any` — name the two fields that are read instead.
type ExportFailure = {
  response?: { status?: number; data?: unknown };
};

// Export responses are requested as blobs, so a *failure* body arrives as a
// Blob too: `err.response.data.error` is always undefined, and the popover
// used to fall back to axios' own "Request failed with status code 403".
// data-svc puts a sentence written for the user in that body (see
// forecast_export_svc.rs, NoviSubscriptionRequired / ForecastSubscriptionRequired),
// so read the body and show what it says.
//
// Only a 403 carries such a body. Every other failure of the export handler
// goes through a generic catch-all answering `400 {"error": e.to_string()}`
// with a raw anyhow message — "Forecast export unavailable: well header store
// is not loaded", Mongo errors, and so on. Rendering those would leak internal
// text the old code never surfaced, and would also slip past the Sentry gate
// below, which keys off the fallback. So anything but a 403 falls back.
//
// `text()` is duck-typed rather than gated on `instanceof Blob` because jsdom's
// Blob has no `text()` — an instanceof check would make this path untestable —
// and a body that never went through the blob transform is worth reading anyway.
const readExportError = async (error: unknown): Promise<string> => {
  const response = (error as ExportFailure)?.response;

  if (response?.status !== 403) {
    return EXPORT_ERROR_FALLBACK;
  }

  const body = response.data as { error?: unknown; text?: () => Promise<string> };

  if (typeof body?.error === "string") {
    return body.error;
  }
  if (typeof body?.text !== "function") {
    return EXPORT_ERROR_FALLBACK;
  }

  try {
    // Type-checked like the branch above: `JSON.parse` returns `any`, and a
    // non-string `error` would reach setError and render as a React child.
    const parsed = JSON.parse(await body.text());
    return typeof parsed?.error === "string" ? parsed.error : EXPORT_ERROR_FALLBACK;
  } catch {
    // Body was not the JSON envelope the service documents.
    return EXPORT_ERROR_FALLBACK;
  }
};

// The three export triggers (ValNav CSV, ValNav XML, Mosaic CSV) differ only in
// the format they ask for, so success and failure are handled here once.
function useExportForecastsMutation(
  format: "CSV" | "XML",
  setError: (message: string | null) => void
) {
  return useMutation(
    async (input: ExportForecastRequest) => await exportForecasts({ ...input, format }),
    {
      onMutate: () => {
        setError(null);
      },
      onSuccess: (data) => {
        exportFile(data);
      },
      onError: async (error) => {
        const message = await readExportError(error);
        // A subscription denial is an expected answer on a path users hit
        // routinely — it belongs in the popover and nowhere else. Falling back
        // means the request never landed, or answered something that cannot be
        // shown; those are the ones worth logging and reporting.
        if (message === EXPORT_ERROR_FALLBACK) {
          // eslint-disable-next-line no-console
          console.error(error);
          Sentry.captureException(error, {
            tags: { feature: "forecast-export", operation: "download" },
            extra: { format }
          });
        }
        setError(message);
      }
    }
  );
}

function ExportButton({ onExport, exporting, children }) {
  return (
    <TypeWellAction
      disabled={exporting}
      onClick={async (evt) => {
        await onExport();
        evt.stopPropagation();
        evt.preventDefault();
      }}>
      {exporting ? (
        <>
          <IconSpinner />
          <span>Exporting...</span>
        </>
      ) : (
        children
      )}
    </TypeWellAction>
  );
}

export enum WellSource {
  McDanielResearch = "EVAResearchForecasts",
  TypeWells = "TypeWells",
  Novi = "NoviForecasts",
  UserForecasts = "UserForecasts"
}

export interface ExportForecastsModel {
  wellSource: WellSource;
  node?;
  type?: "folder" | "forecast" | "selected";
}

const getAllTypeWellsInFolder = (node): TypeWellId[] => {
  if (!node) return [];

  let typeWells: TypeWellId[] = [];

  if (!node.isFolder && node.type !== "folder") {
    typeWells.push({
      id: node.id,
      name: node.title,
      folderId: node.folderId
    });
  }

  if (node.children && node.children.length > 0) {
    node.children.forEach((child) => {
      typeWells = typeWells.concat(getAllTypeWellsInFolder(child));
    });
  }

  return typeWells;
};

// Shared with FolderNodeActions.tsx so the "should we even show the export
// action" check uses the exact same folder/forecast traversal as the export
// request itself (see getAllTypeWellsInFolder above) instead of a second,
// possibly-diverging implementation. Short-circuits on the first exportable
// forecast found rather than building the full list.
export const hasExportableForecasts = (node): boolean => {
  if (!node) return false;

  if (!node.isFolder && node.type !== "folder") {
    return true;
  }

  return !!node.children?.some((child) => hasExportableForecasts(child));
};

function ExportForecastsButton({ wellSource, node, type }: ExportForecastsModel) {
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const selectedTypeWell = useForecastStore(selectSelectedTypeWell);
  const filterId = useSelector((state: RootState) => state.filter.filterId);
  const resourcePlays = useSelector((state: RootState) => state.filter.currentPlayList);
  // The filter's count, not the legend sum, which leaves out wells Hide Others hides.
  const filterCounts = useSelector((state: RootState) => state.filter.filterCounts);
  const currentFilterWellCount = filterCounts?.Active ?? filterCounts?.UniqueId ?? 0;

  const { hasTrialAccount, isReadonly } = useUser();
  const { sendExportForecastsEvent } = useUserEvents();

  const [error, setError] = useState<string | null>(null);

  const { data: exportForecastPreference, refetch } =
    useUserSettingFetcher<ForecastExportSetting>(FORECAST_EXPORT_SETTING);

  const enableValNav = exportForecastPreference?.enableValNav ?? true;
  const enableMosaic = exportForecastPreference?.enableMosaic ?? true;

  const canExport = !isReadonly && !hasTrialAccount;

  const csvExportValNavMutation = useExportForecastsMutation("CSV", setError);
  const xmlExportValNavMutation = useExportForecastsMutation("XML", setError);
  const csvExportMosaicMutation = useExportForecastsMutation("CSV", setError);

  useEffect(() => {
    refetch();
  }, [refetch]);

  if (
    !canExport ||
    (exportForecastPreference &&
      !exportForecastPreference.enableMosaic &&
      !exportForecastPreference.enableValNav)
  ) {
    return null;
  }

  function getTypeWells(): TypeWellId[] {
    switch (type) {
      case "selected":
        return selectedTypeWell
          ? [
              {
                id: selectedTypeWell.id,
                name: selectedTypeWell.title,
                folderId: selectedTypeWell.folderId
              }
            ]
          : [];
      case "folder":
        return node ? getAllTypeWellsInFolder(node) : [];
      case "forecast":
        return node
          ? [
              {
                id: node.id,
                name: node.title,
                folderId: node.folderId
              }
            ]
          : [];
      default:
        return wellSource == WellSource.TypeWells
          ? checkedGlobalTypeWells.map((tw) => ({
              id: tw.id,
              name: tw.title,
              folderId: tw.folderId
            }))
          : [];
    }
  }

  return (
    <>
      <Popover
        overlayClassName="export-popover"
        placement="right"
        trigger="click"
        content={
          <PopoverContentWrapper>
            {enableValNav && enableMosaic && <GroupLabel>ValNav</GroupLabel>}
            {enableValNav && (
              <ExportButton
                onExport={() => {
                  const data: ExportForecastRequest = {
                    filterId: filterId.id,
                    typeWellIds: getTypeWells(),
                    settings: {
                      source: wellSource.toString(),
                      thirdPartyFormat: "ValNav"
                    }
                  };

                  // Send user event for forecast export
                  sendExportForecastsEvent({
                    format: "CSV",
                    thirdPartyFormat: "ValNav",
                    source: wellSource.toString(),
                    resourcePlays: resourcePlays,
                    wellCount: currentFilterWellCount
                  });

                  csvExportValNavMutation.mutate(data);
                }}
                exporting={csvExportValNavMutation.isLoading}>
                <Icon path={mdiDownload} size={"18px"} />
                <span>Export to CSV (.csv)</span>
              </ExportButton>
            )}
            {enableValNav && (
              <ExportButton
                onExport={() => {
                  const data: ExportForecastRequest = {
                    filterId: filterId.id,
                    typeWellIds: getTypeWells(),
                    settings: {
                      source: wellSource.toString(),
                      thirdPartyFormat: "ValNav"
                    }
                  };

                  // Send user event for forecast export
                  sendExportForecastsEvent({
                    format: "XML",
                    thirdPartyFormat: "ValNav",
                    source: wellSource.toString(),
                    resourcePlays: resourcePlays,
                    wellCount: currentFilterWellCount
                  });

                  xmlExportValNavMutation.mutate(data);
                }}
                exporting={xmlExportValNavMutation.isLoading}>
                <Icon path={mdiDownload} size={"18px"} />
                <span>Export to XML (.xml)</span>
              </ExportButton>
            )}
            {enableValNav && enableMosaic && <GroupLabel>Mosaic</GroupLabel>}
            {enableMosaic && (
              <ExportButton
                onExport={() => {
                  const data: ExportForecastRequest = {
                    filterId: filterId.id,
                    typeWellIds: getTypeWells(),
                    settings: {
                      source: wellSource.toString(),
                      thirdPartyFormat: "Mosaic"
                    }
                  };

                  // Send user event for forecast export
                  sendExportForecastsEvent({
                    format: "CSV",
                    thirdPartyFormat: "Mosaic",
                    source: wellSource.toString(),
                    resourcePlays: resourcePlays,
                    wellCount: currentFilterWellCount
                  });

                  csvExportMosaicMutation.mutate(data);
                }}
                exporting={csvExportMosaicMutation.isLoading}>
                <Icon path={mdiDownload} size={"18px"} />
                <span>Export to CSV (.csv)</span>
              </ExportButton>
            )}
            {error && <ErrorComponent role="alert">{error}</ErrorComponent>}
          </PopoverContentWrapper>
        }>
        {wellSource == WellSource.McDanielResearch || wellSource == WellSource.Novi ? (
          <Tooltip placement="top" title="Export Forecasts">
            <ExportForecastActionButton className="export-action-button">
              <Icon path={mdiDownload} size={1.4} />
            </ExportForecastActionButton>
          </Tooltip>
        ) : (
          <TypeWellAction
            onClick={(evt) => {
              evt.stopPropagation();
              evt.preventDefault();
            }}>
            <Icon path={mdiDownload} size={"18px"} />
            <span>Download</span>
          </TypeWellAction>
        )}
      </Popover>
    </>
  );
}

export default ExportForecastsButton;

const TypeWellAction = styled.div`
  height: 3.2rem;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text-50);
  font-weight: 500;
  padding: 0 var(--space-3);
  cursor: pointer;
  &:hover {
    color: ${(props) => (props.danger ? "var(--color-danger)" : "var(--color-primary)")};
  }
`;

const ExportForecastActionButton = styled.button`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  padding: 0;
  margin: 3px;
  cursor: pointer;
  background: transparent;
  color: ${(props) => (props.disabled ? "#ddd" : "#a2aaad")};

  &:hover {
    color: ${(props) =>
      props.disabled
        ? "#ddd"
        : props.danger
        ? "var(--color-danger)"
        : "var(--color-primary)"};
  }

  &[disabled]:hover > svg {
    cursor: default;
  }
`;

const GroupLabel = styled.div`
  margin-left: 12px;
  color: var(--color-text-50);
`;

const PopoverContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px;
  font-size: 14px;

  ${GroupLabel}:first-child {
    padding-top: 5px;
  }
`;

const ErrorComponent = styled.div`
  /* The popover sizes to its content, so an unwrapped sentence would stretch it
     off-screen. white-space does the wrapping; this caps how wide the popover
     is allowed to get before that starts. */
  color: var(--color-danger);
  max-width: 32rem;
  padding: var(--space-2) var(--space-3);
  line-height: 1.4;
  white-space: normal;
`;
